import React, { useCallback, useEffect, useState } from "react";
import { Button, DatePicker, Space, Spin, notification } from "antd";
import {
  BarChartOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import moment from "moment";

import { getDashboardSummary } from "../../../../controllers/V1/smartRtp/smartRtpController";
import { formatSmartRtpNumber } from "../../../../utils/bigMath";

function fmtQty(value) {
  return Number(value || 0).toLocaleString("en-US");
}

function fmtMoney(value) {
  return formatSmartRtpNumber(value || 0, 3);
}

function MetricLine({ label, value }) {
  return (
    <div style={{ marginBottom: 8, color: "#444", fontSize: 14 }}>
      {label} = <strong>{value}</strong>
    </div>
  );
}

function SummaryCard({ title, accent, children, hero }) {
  return (
    <div
      className="card h-100"
      style={{
        border: "1px solid #eef0f4",
        borderTop: `3px solid ${accent}`,
        borderRadius: 8,
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      }}
    >
      <div className="card-body">
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: 0.4,
            color: "#8a8f98",
            marginBottom: hero ? 4 : 12,
            textTransform: "uppercase",
          }}
        >
          {title}
        </div>
        {hero ? (
          <div
            style={{
              fontSize: 28,
              fontWeight: 700,
              color: accent,
              marginBottom: 12,
              lineHeight: 1.2,
            }}
          >
            {hero}
          </div>
        ) : null}
        {children}
      </div>
    </div>
  );
}

/**
 * Instant-Win style daily Summary for a single game (Game Settings).
 */
export default function GameSettingsSummary({ gameId }) {
  const [filterDate, setFilterDate] = useState(() => dayjs());
  const [loading, setLoading] = useState(false);
  const [metrics, setMetrics] = useState(null);

  const loadSummary = useCallback(async () => {
    if (!gameId) {
      setMetrics(null);
      return;
    }
    setLoading(true);
    try {
      const res = await getDashboardSummary({
        game_id: gameId,
        filter_date: filterDate ? filterDate.format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
      });
      if (res?.status === true) {
        setMetrics(res.result?.metrics || res.result || {});
      } else {
        setMetrics(null);
        notification.error({
          message: "Summary",
          description: res?.message || "Unable to load summary.",
          duration: 2,
        });
      }
    } catch (error) {
      setMetrics(null);
      notification.error({
        message: "Summary",
        description: "Unable to load summary.",
        duration: 2,
      });
    } finally {
      setLoading(false);
    }
  }, [gameId, filterDate]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const handleExport = () => {
    if (!metrics) {
      notification.warning({
        message: "Export",
        description: "Load summary first.",
        duration: 2,
      });
      return;
    }
    const dateLabel = filterDate ? filterDate.format("YYYY-MM-DD") : moment().format("YYYY-MM-DD");
    const rows = [
      ["Metric", "Value"],
      ["Filter Date", dateLabel],
      ["Game Price (QR)", metrics.game_price ?? 0],
      ["Final Sales Count (QTY)", metrics.final_sales_count ?? metrics.total_tickets ?? 0],
      ["Total Revenue (QR)", metrics.total_revenue ?? metrics.total_stake ?? 0],
      ["Total Amount Per Pool (QR)", metrics.total_amount_per_pool ?? metrics.total_rewards ?? 0],
      ["Target RTP (%)", metrics.target_rtp ?? 0],
      ["RTP Distributed (%)", metrics.rtp_distributed_percent ?? metrics.actual_rtp ?? 0],
      ["RTP Variance (%)", metrics.rtp_variance ?? 0],
      ["RTP Rewards Distributed (QR)", metrics.rtp_distributed_amount ?? metrics.total_rewards ?? 0],
      ["Win Orders", metrics.win_orders ?? 0],
      ["Scheduled Big Prize (QR)", metrics.scheduled_big_prize_amount ?? 0],
      ["Profit Amount (QR)", metrics.profit_amount ?? 0],
      ["Loss Amount (QR)", metrics.loss_amount ?? 0],
      ["Profit Margin (%)", metrics.profit_margin ?? 0],
      ["Total Big Prize Amount (QR)", metrics.total_big_prize_amount ?? metrics.scheduled_big_prize_amount ?? 0],
      ["Orders", metrics.total_orders ?? 0],
      ["Cancelled", metrics.cancelled_orders ?? 0],
      ["Sales", metrics.total_revenue ?? metrics.total_stake ?? 0],
      ["Winning", metrics.total_amount_per_pool ?? metrics.total_rewards ?? 0],
      ["Redeemed", metrics.redeemed_amount ?? 0],
    ];
    const sheet = XLSX.utils.aoa_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Summary");
    XLSX.writeFile(workbook, `Smart RTP Game Summary ${dateLabel}.xlsx`);
  };

  if (!gameId) return null;

  const m = metrics || {};
  const revenue = m.total_revenue ?? m.total_stake ?? 0;
  const poolAmount = m.total_amount_per_pool ?? m.total_rewards ?? 0;
  const bigPrize = m.total_big_prize_amount ?? m.scheduled_big_prize_amount ?? 0;
  const targetRtp = m.target_rtp ?? 0;
  const actualRtp = m.rtp_distributed_percent ?? m.actual_rtp ?? 0;
  const rtpVariance = m.rtp_variance ?? 0;
  const rtpDistributed = m.rtp_distributed_amount ?? m.total_rewards ?? 0;

  return (
    <div className="card shadow mb-4">
      <div className="card-body">
        <h5
          style={{
            marginBottom: 16,
            display: "flex",
            alignItems: "center",
            gap: 8,
            color: "#333",
          }}
        >
          <BarChartOutlined style={{ color: "#6f42c1" }} />
          Summary
        </h5>

        <div
          className="d-flex flex-wrap align-items-center gap-2 mb-3"
          style={{ rowGap: 8 }}
        >
          <span style={{ color: "#555", fontWeight: 500 }}>Filter by Date:</span>
          <DatePicker
            value={filterDate}
            onChange={(value) => setFilterDate(value || dayjs())}
            format="MM/DD/YYYY"
            allowClear={false}
          />
          <Space>
            <Button
              type="default"
              onClick={loadSummary}
              loading={loading}
              style={{ borderColor: "#6f42c1", color: "#6f42c1" }}
            >
              Get Summary
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExport}
              style={{ background: "#28a745", borderColor: "#28a745" }}
            >
              Export Excel
            </Button>
          </Space>
        </div>

        <Spin spinning={loading}>
          <div className="row">
            <div className="col-lg-3 col-md-6 mb-3">
              <SummaryCard
                title="Game Price"
                accent="#6f42c1"
                hero={`${fmtMoney(m.game_price)} QR`}
              >
                <MetricLine
                  label="Final Sales Count (QTY)"
                  value={fmtQty(m.final_sales_count ?? m.total_tickets)}
                />
                <MetricLine label="Total Revenue (QR)" value={fmtMoney(revenue)} />
              </SummaryCard>
            </div>
            <div className="col-lg-3 col-md-6 mb-3">
              <SummaryCard
                title="RTP Distribution"
                accent="#fd7e14"
                hero={`${fmtMoney(actualRtp)}%`}
              >
                <MetricLine label="Target RTP" value={`${fmtMoney(targetRtp)}%`} />
                <MetricLine
                  label="RTP Distributed"
                  value={`${fmtMoney(actualRtp)}%`}
                />
                <MetricLine
                  label="RTP Variance"
                  value={`${rtpVariance > 0 ? "+" : ""}${fmtMoney(rtpVariance)}%`}
                />
                <MetricLine
                  label="RTP Rewards Distributed (QR)"
                  value={fmtMoney(rtpDistributed)}
                />
                <MetricLine label="Win Orders" value={fmtQty(m.win_orders)} />
                <div style={{ fontSize: 12, color: "#8a8f98", marginTop: 4 }}>
                  Excludes scheduled big prizes &amp; time slot awards
                </div>
              </SummaryCard>
            </div>
            <div className="col-lg-3 col-md-6 mb-3">
              <SummaryCard title="Payout & Profit" accent="#28a745">
                <MetricLine
                  label="Total Amount Per Pool (QR)"
                  value={fmtMoney(poolAmount)}
                />
                <MetricLine
                  label="Profit Amount (QR)"
                  value={fmtMoney(m.profit_amount)}
                />
                <MetricLine
                  label="Loss Amount (QR)"
                  value={fmtMoney(m.loss_amount)}
                />
                <MetricLine
                  label="Profit Margin"
                  value={`${fmtMoney(m.profit_margin)}%`}
                />
                <MetricLine
                  label="Total Big Prize Amount (QR)"
                  value={fmtMoney(bigPrize)}
                />
              </SummaryCard>
            </div>
            <div className="col-lg-3 col-md-6 mb-3">
              <SummaryCard title="Live Sales" accent="#0d6efd">
                <MetricLine label="Orders" value={fmtQty(m.total_orders)} />
                <MetricLine label="Cancelled" value={fmtQty(m.cancelled_orders)} />
                <MetricLine label="Sales" value={fmtMoney(revenue)} />
                <MetricLine label="Winning" value={fmtMoney(poolAmount)} />
                <MetricLine label="Redeemed" value={fmtMoney(m.redeemed_amount)} />
                <MetricLine
                  label="Scheduled Big Prizes"
                  value={fmtQty(m.scheduled_big_prize_count)}
                />
              </SummaryCard>
            </div>
          </div>
        </Spin>
      </div>
    </div>
  );
}
